import React, { Suspense, useState, useEffect, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Environment, ContactShadows, RandomizedLight } from "@react-three/drei";
import { LayerMaterial, Color, Depth } from "lamina";
import Sofa from "./Sofa";
import "./style.css";
import * as THREE from "three";

export default function Viewer() {
  const [scale, setScale] = useState(1);

  const canvasRef = useRef();
  const [showOverlay, setShowOverlay] = useState(false);
  const interactionTimeout = useRef(null);

  const resetInteractionTimer = () => {
    clearTimeout(interactionTimeout.current);
    setShowOverlay(false);
    interactionTimeout.current = setTimeout(() => {
      setShowOverlay(true);
    }, 2000); // 2 seconds of inactivity
  };

  useEffect(() => {
    const canvasElement = canvasRef.current;
    canvasElement.addEventListener("mousemove", resetInteractionTimer);
    canvasElement.addEventListener("touchstart", resetInteractionTimer);

    resetInteractionTimer();

    return () => {
      canvasElement.removeEventListener("mousemove", resetInteractionTimer);
      canvasElement.removeEventListener("touchstart", resetInteractionTimer);
      clearTimeout(interactionTimeout.current);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setScale(5);
      } else {
        setScale(10);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="viewer-container" ref={canvasRef}>
      <h1 className="viewer-heading">Sofa Showcase</h1>
      <p className="viewer-subheading">Try products in your place or on your body</p>
      <div className="canvas-container">
        {showOverlay && <img src="Cursor-Gif_6.gif" alt="Inactivity Overlay" className="inactivity-overlay" />}
        <Suspense fallback={null}>
          <Canvas shadows camera={{ position: [0, 25, 50], fov: 30 }}>
            <ambientLight intensity={1.5} />
            <Environment
              files="/christmas_photo_studio_05_2k.hdr" // HDR environment map
              background={false} // Use lighting from the HDR but don't show it in the background
            />
            <ContactShadows temporal frames={60} scale={200} position={[0, -5.0, 0]}>
              <RandomizedLight amount={8} radius={10} ambient={0.5} position={[1, 5, -1]} />
            </ContactShadows>

            <BackgroundSphere />
            <Sofa scale={[scale, scale, scale]} position={[0, -5.0, 0]} />
            <OrbitControls
              enableZoom={false}
              enablePan={false}
              autoRotate
              enableDamping={true}
              dampingFactor={0.1}
              makeDefault
            />
          </Canvas>
        </Suspense>
      </div>
      <a href="https://calendly.com/vincent-119" target="_blank" rel="noopener noreferrer" className="viewer-demo-button">
        Book a Demo
      </a>
    </div>
  );
}

function BackgroundSphere() {
  return (
    <mesh scale={200}> {/* Increased scale to push the edges further away */}
      <sphereGeometry args={[1, 64, 64]} />
      <LayerMaterial side={THREE.BackSide}>
        {/* Use a very light color for the base */}
        <Color color="#f5f5f5" alpha={1} mode="normal" />
        {/* Adjust Depth layer to reduce contrast at edges */}
        <Depth
          colorA="white" // Near color (light shade)
          colorB="skyblue" // Far color (subtle gradient)
          alpha={0.2} // Softer effect
          mode="normal"
          near={0}
          far={500} // Increased depth range for a smoother gradient
          origin={[0, 0, 0]} // Keep the origin consistent
        />
      </LayerMaterial>
    </mesh>
  );
}


